import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircleOutline,
  DoneAll,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { useGetAnnouncements } from '../Hooks/useGetAnnouncements';

// ─── Severity config ──────────────────────────────────────────────────────────

const SEVERITY = {
  info: {
    dot:   'primary.main',
    chip:  { bgcolor: 'primary.50',  color: 'primary.800',  label: 'Info'    },
    border:'primary.main',
  },
  warning: {
    dot:   'warning.main',
    chip:  { bgcolor: 'warning.50',  color: 'warning.800',  label: 'Warning' },
    border:'warning.main',
  },
  error: {
    dot:   'error.main',
    chip:  { bgcolor: 'error.50',    color: 'error.800',    label: 'Urgent'  },
    border:'error.main',
  },
};

function severityFor(key) {
  return SEVERITY[key] ?? SEVERITY.info;
}

// ─── Filter options ───────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'all',     label: 'All'     },
  { key: 'unread',  label: 'Unread'  },
  { key: 'info',    label: 'Info'    },
  { key: 'warning', label: 'Warning' },
  { key: 'error',   label: 'Urgent'  },
];

// ─── Single announcement row ──────────────────────────────────────────────────

function AnnouncementItem({ announcement, onAcknowledge }) {
  const sev     = severityFor(announcement.severity);
  const isRead  = announcement.ack;

  return (
    <ListItem
      disablePadding
      sx={{
        display:         'flex',
        flexDirection:   'column',
        alignItems:      'stretch',
        border:          '0.5px solid',
        borderColor:     'divider',
        borderRadius:    2,
        p:               1.75,
        mb:              1,
        bgcolor:         isRead ? 'action.hover' : 'background.paper',
        opacity:         isRead ? 0.6 : 1,
        transition:      'opacity 0.2s',
      }}
    >
      {/* ── Top row: dot + title + severity chip ── */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 0.75 }}>
        <Stack direction="row" alignItems="flex-start" gap={1} sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width:       8,
              height:      8,
              borderRadius:'50%',
              bgcolor:     sev.dot,
              flexShrink:  0,
              mt:          0.7,
            }}
          />
          <Typography
            variant="body2"
            fontWeight={isRead ? 400 : 500}
            sx={{ lineHeight: 1.4, wordBreak: 'break-word' }}
          >
            {announcement.title}
          </Typography>
        </Stack>

        <Chip
          label={sev.chip.label}
          size="small"
          sx={{
            fontSize:  10,
            height:    20,
            flexShrink:0,
            bgcolor:   sev.chip.bgcolor,
            color:     sev.chip.color,
            border:    'none',
          }}
        />
      </Stack>

      {/* ── Body ── */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ pl: 2.25, mb: 1, lineHeight: 1.6, wordBreak: 'break-word' }}
      >
        {announcement.body}
      </Typography>

      {/* ── Footer: meta + action ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
        sx={{ pl: 2.25 }}
      >
        <Typography variant="caption" color="text.disabled">
          {DateTime.fromISO(announcement.created_at).toFormat('LLL dd, yyyy · h:mm a')}
          {' · '}
          <Typography component="span" variant="caption" color="text.secondary" fontWeight={500}>
            {announcement.staff_name}
          </Typography>
        </Typography>

        {!isRead ? (
          <Button
            size="small"
            variant="outlined"
            color="success"
            disableElevation
            startIcon={<CheckCircleOutline sx={{ fontSize: '14px !important' }} />}
            onClick={() => onAcknowledge(announcement.id)}
            sx={{ textTransform: 'none', fontSize: 12, py: 0.4, flexShrink: 0 }}
          >
            Acknowledge
          </Button>
        ) : (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <DoneAll sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">Read</Typography>
          </Stack>
        )}
      </Stack>
    </ListItem>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const Announcements = () => {
  const locations = useSelector((state) => state.role.tutor_locations);
  const programs  = useSelector((state) => state.role.tutor_programs);

  const { announcements, fetchAnnouncements, ackAnnouncments, loading } = useGetAnnouncements();

  const [activeFilter, setActiveFilter] = useState('all');

  const getAnnouncements = async () => {
    const location_ids = locations?.map((l) => l.id).join(',') ?? null;
    const program_ids  = programs?.map((p) => p.id).join(',')  ?? null;
    await fetchAnnouncements(location_ids, program_ids);
  };

  useEffect(() => { getAnnouncements(); }, []);

  // Sort: most recent first
  const sorted = useMemo(() => (
    [...announcements].sort((a, b) =>
      DateTime.fromISO(b.created_at).toMillis() - DateTime.fromISO(a.created_at).toMillis()
    )
  ), [announcements]);

  // Apply filter
  const filtered = useMemo(() => {
    switch (activeFilter) {
      case 'unread':  return sorted.filter((a) => !a.ack);
      case 'info':    return sorted.filter((a) => a.severity === 'info');
      case 'warning': return sorted.filter((a) => a.severity === 'warning');
      case 'error':   return sorted.filter((a) => a.severity === 'error');
      default:        return sorted;
    }
  }, [sorted, activeFilter]);

  const unreadCount = sorted.filter((a) => !a.ack).length;

  const handleAcknowledge = async (id) => {
    await ackAnnouncments(id);
  };

  return (
    <Box sx={{ mt: 2 }}>
      
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight={600}>
            Announcements
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} unread`}
              size="small"
              color="error"
              sx={{ fontSize: 11, height: 20 }}
            />
          )}
        </Stack>
      </Stack>

      {/* ── Filter pills ── */}
      <Stack direction="row" gap={0.75} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            onClick={() => setActiveFilter(f.key)}
            variant={activeFilter === f.key ? 'filled' : 'outlined'}
            color={activeFilter === f.key ? 'primary' : 'default'}
            sx={{ fontSize: 12, cursor: 'pointer' }}
          />
        ))}
      </Stack>

      {/* ── Feed ── */}
      {loading ? 
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress/>
      </Box>
      :
      <List disablePadding>
        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body2" color="text.secondary">
              Nothing to show here.
            </Typography>
          </Box>
        ) : (
          filtered.map((announcement) => (
            <AnnouncementItem
              key={announcement.id}
              announcement={announcement}
              onAcknowledge={handleAcknowledge}
            />
          ))
        )}
      </List>}
    </Box>
  );
};

export default Announcements;